/* ═══════════════════════════════════════════════════════════
   Ki'olal Welcome Experience behaviour
   Approved 2026-08-09 by Jessica Treviño Rojas
   Specification: docs/design/welcome-popup-specification-2026-08-06.md
   Corrected contract:
   docs/design/welcome-popup-corrected-implementation-contract-2026-08-09.md

   Production states: idle -> submitting -> (success | member | failure)
   Suppression: session-only after an ordinary close; permanent only after a
   confirmed signup or a recognised existing member. A service failure sets no
   suppression at all.

   The unpublished draft exposes an explicit review-only mode through
   `kiolal_popup_review=1`. Review mode never calls the secure endpoint, never
   stores permanent suppression, and never represents simulated outcomes as real.
   ═══════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  var SESSION_KEY = 'kiolal_welcome_session_closed';
  var PERSISTENT_KEY = 'kiolal_welcome_suppressed';
  var REVIEW_PARAM = 'kiolal_popup_review';
  var RESET_PARAM = 'kiolal_popup_reset';
  var OUTCOME_PARAM = 'kiolal_popup_outcome';
  var EXTERNAL_SUPPRESSION_ATTRIBUTE = 'data-kiolal-review-external-suppressed';
  var REQUEST_TIMEOUT_MILLISECONDS = 8000;

  var MESSAGES = {
    name: 'Please enter your first name.',
    emailEmpty: 'Please enter your email address.',
    emailInvalid: 'Please enter a valid email address.',
    emailConsent: 'Please confirm your email consent to continue.',
    phoneForSms: 'Please enter your mobile number so we can text you your code.',
    phoneInvalid: 'Please select a country code and enter a valid phone number.',
    smsConsent: 'Please agree to receive texts so we can send your code.',
    failure: "We couldn't complete your signup. Please try again."
  };

  var EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  var FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function getReviewConfig(root) {
    var config = {
      active: false,
      reset: false,
      outcome: 'failure'
    };

    try {
      var params = new URLSearchParams(window.location.search);
      var requestedOutcome = (params.get(OUTCOME_PARAM) || '').toLowerCase();
      var expectedThemeId = root.getAttribute('data-review-theme-id') || '';
      var activeTheme = window.Shopify && window.Shopify.theme;
      var isExactUnpublishedTheme = activeTheme &&
        String(activeTheme.id) === expectedThemeId &&
        activeTheme.role !== 'main';

      config.active = params.get(REVIEW_PARAM) === '1' && isExactUnpublishedTheme;
      config.reset = params.get(RESET_PARAM) === '1';

      if (requestedOutcome === 'success') {
        config.outcome = 'subscribed';
      } else if (requestedOutcome === 'member') {
        config.outcome = 'existing_member';
      }
    } catch (error) {
      /* Unsupported URL parsing fails closed: review mode stays inactive. */
    }

    return config;
  }

  /**
   * The single privileged integration seam.
   *
   * The secure service verifies Shopify proxy context, checks every supplied
   * identifier against list UkRnxy, rejects mixed identities, records the
   * separately requested single-opt-in channels, and returns bounded states only
   * after server confirmation. Browser JavaScript never receives a Klaviyo
   * private credential.
   *
   * Review mode is deterministic and non-production. It never calls the service
   * and exists only so the approved success, member, and failure states can be
   * reviewed on unpublished theme 157988618340.
   */
  function createIdempotencyKey() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }

    if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
      var bytes = new Uint8Array(24);
      window.crypto.getRandomValues(bytes);
      return Array.prototype.map.call(bytes, function (value) {
        return value.toString(16).padStart(2, '0');
      }).join('');
    }

    return '';
  }

  function normalizeEnrollmentResponse(response, body) {
    if (response.status === 200 && body && body.state === 'accepted') {
      return { outcome: 'subscribed' };
    }


    if (response.status === 409 && body && body.state === 'existing_member') {
      return { outcome: 'existing_member' };
    }

    return { outcome: 'failure' };
  }

  function submitWelcomeSignup(payload, review, endpoint) {
    if (review.active) {
      return new Promise(function (resolve) {
        window.setTimeout(function () {
          resolve({ outcome: review.outcome, simulated: true });
        }, 280);
      });
    }

    if (!endpoint || !endpoint.siteId || !endpoint.listId || typeof window.fetch !== 'function') {
      return Promise.resolve({ outcome: 'failure' });
    }

    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timeout;
    var timeoutPromise = new Promise(function (resolve) {
      timeout = window.setTimeout(function () {
        if (controller) {
          controller.abort();
        }
        resolve({ outcome: 'failure' });
      }, REQUEST_TIMEOUT_MILLISECONDS);
    });

    // 10/2026: the kiolal-community app proxy stopped answering (503), so sign-ups
    // now go straight to Klaviyo's public client subscriptions API.
    var profileAttributes = { email: payload.email };
    if (payload.name) profileAttributes.first_name = payload.name;
    var subscriptions = {};
    if (payload.emailConsent) subscriptions.email = { marketing: { consent: 'SUBSCRIBED' } };
    if (payload.phone && payload.smsConsent) {
      profileAttributes.phone_number = payload.phone;
      subscriptions.sms = { marketing: { consent: 'SUBSCRIBED' } };
    }
    profileAttributes.subscriptions = subscriptions;
    var klaviyoBody = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: payload.source || 'kiolal-welcome-popup',
          profile: { data: { type: 'profile', attributes: profileAttributes } }
        },
        relationships: { list: { data: { type: 'list', id: endpoint.listId } } }
      }
    };

    var requestPromise = window.fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(endpoint.siteId), {
      method: 'POST',
      cache: 'no-store',
      headers: {
        'Accept': 'application/vnd.api+json',
        'Content-Type': 'application/vnd.api+json',
        'revision': '2025-01-15'
      },
      body: JSON.stringify(klaviyoBody),
      signal: controller ? controller.signal : undefined
    }).then(function (response) {
      if (response.status === 202 || response.status === 200) {
        return { outcome: 'subscribed' };
      }
      return { outcome: 'failure' };
    }, function () {
      return { outcome: 'failure' };
    });

    return Promise.race([requestPromise, timeoutPromise]).then(function (result) {
      window.clearTimeout(timeout);
      return result;
    });
  }

  function readStorage(storage, key) {
    try {
      return window[storage].getItem(key);
    } catch (error) {
      return null;
    }
  }

  function writeStorage(storage, key, value) {
    try {
      window[storage].setItem(key, value);
    } catch (error) {
      /* Storage is unavailable in private modes; suppression degrades safely. */
    }
  }

  function removeStorage(storage, key) {
    try {
      window[storage].removeItem(key);
    } catch (error) {
      /* Review reset is best-effort when storage is unavailable. */
    }
  }

  function composeE164Phone(countryCode, localNumber) {
    var code = (countryCode || '').trim();
    var number = (localNumber || '').trim();

    if (!number) {
      return null;
    }

    if (!/^\+[1-9]\d{0,2}$/.test(code)) {
      return '';
    }

    if (!/^[0-9()\s.-]+$/.test(number)) {
      return '';
    }

    var digits = number.replace(/[()\s.-]/g, '');
    var composed = code + digits;

    return /^\+[1-9]\d{7,14}$/.test(composed) ? composed : '';
  }

  function containsExternalAiCampaignText(node) {
    var text = (node.textContent || '').replace(/\s+/g, ' ').toLowerCase();

    return text.indexOf('ai skin analysis') !== -1 ||
      text.indexOf('start your skin analysis') !== -1;
  }

  function findExternalCampaignContainer(node) {
    var current = node;
    var fallback = node;

    while (current && current !== document.body) {
      var style = window.getComputedStyle(current);
      var rect = current.getBoundingClientRect();

      fallback = current;

      if (
        style.position === 'fixed' &&
        rect.width >= window.innerWidth * 0.45 &&
        rect.height >= window.innerHeight * 0.35
      ) {
        return current;
      }

      current = current.parentElement;
    }

    return fallback;
  }

  function suppressKnownExternalCampaigns(root, review) {
    if (!review.active) {
      return;
    }

    var suppressionStyle = document.getElementById('kiolalWelcomeReviewSuppression');
    if (!suppressionStyle) {
      suppressionStyle = document.createElement('style');
      suppressionStyle.id = 'kiolalWelcomeReviewSuppression';
      suppressionStyle.textContent = '[' + EXTERNAL_SUPPRESSION_ATTRIBUTE + '="true"] { display: none !important; visibility: hidden !important; }';
      document.head.appendChild(suppressionStyle);
    }

    function inspect() {
      var candidates = document.querySelectorAll('.klaviyo-form, [class*="klaviyo-form-version-"]');
      var suppressed = 0;

      Array.prototype.forEach.call(candidates, function (candidate) {
        if (root.contains(candidate) || !containsExternalAiCampaignText(candidate)) {
          return;
        }

        var container = findExternalCampaignContainer(candidate);
        container.setAttribute(EXTERNAL_SUPPRESSION_ATTRIBUTE, 'true');
        suppressed += 1;
      });

      root.setAttribute('data-review-external-suppressed-count', String(suppressed));
    }

    inspect();

    var observer = new MutationObserver(inspect);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  function setupWelcome(root) {
    var review = getReviewConfig(root);
    var dialog = root.querySelector('[data-kiolal-welcome-dialog]');
    var panel = root.querySelector('.kiolal-welcome__panel');
    var backdrop = root.querySelector('[data-kiolal-welcome-backdrop]');
    var form = root.querySelector('[data-kiolal-welcome-form]');
    var submit = root.querySelector('[data-kiolal-welcome-submit]');
    var status = root.querySelector('[data-kiolal-welcome-status]');
    var reviewNote = root.querySelector('[data-kiolal-welcome-review-note]');
    var enrollmentEndpoint = {
      siteId: root.getAttribute('data-klaviyo-site-id') || '',
      listId: root.getAttribute('data-klaviyo-list-id') || ''
    };
    var copyButton = root.querySelector('[data-kiolal-welcome-copy]');
    var continueButton = root.querySelector('[data-kiolal-welcome-continue]');
    var closeButtons = root.querySelectorAll('[data-kiolal-welcome-close]');
    var states = root.querySelectorAll('[data-kiolal-welcome-state]');
    var fields = {
      name: root.querySelector('#kiolalWelcomeName'),
      email: root.querySelector('#kiolalWelcomeEmail'),
      country_code: root.querySelector('#kiolalWelcomeCountryCode'),
      phone: root.querySelector('#kiolalWelcomePhone'),
      email_consent: root.querySelector('#kiolalWelcomeEmailConsent'),
      sms_consent: root.querySelector('#kiolalWelcomeConsent')
    };

    var previousFocus = null;
    var isOpen = false;
    var idempotencyKey = null;

    root.setAttribute('data-review-active', review.active ? 'true' : 'false');
    root.setAttribute('data-review-outcome', review.outcome);

    if (reviewNote) {
      reviewNote.hidden = !review.active;
    }

    if (review.active && review.reset) {
      removeStorage('sessionStorage', SESSION_KEY);
      removeStorage('localStorage', PERSISTENT_KEY);
    }

    suppressKnownExternalCampaigns(root, review);

    function showState(name) {
      var activeState = null;
      root.setAttribute('data-active-state', name);

      Array.prototype.forEach.call(states, function (state) {
        state.hidden = state.getAttribute('data-kiolal-welcome-state') !== name;
        if (!state.hidden) {
          activeState = state;
        }
      });

      var heading = activeState && activeState.querySelector('.kiolal-welcome__heading[id]');
      if (heading) {
        dialog.setAttribute('aria-labelledby', heading.id);
      }
    }

    function announce(message) {
      status.textContent = message || '';
    }

    function showConfirmedSuccess(simulated) {
      showState('success');
      if (!review.active) {
        writeStorage('localStorage', PERSISTENT_KEY, '1');
      }
      announce(simulated ? 'Draft review: simulated success state.' : 'Your first ceremonia awaits. Check your texts for your code.');
    }

    function showExistingMember(simulated) {
      showState('member');
      if (!review.active) {
        writeStorage('localStorage', PERSISTENT_KEY, '1');
      }
      announce(simulated ? 'Draft review: simulated existing-member state.' : "You're already part of the Ki'olal Community.");
    }

    function handleEnrollmentResult(result) {
      var outcome = (result && result.outcome) || 'failure';

      if (outcome === 'subscribed') {
        showConfirmedSuccess(Boolean(result && result.simulated));
        return;
      }

      if (outcome === 'existing_member') {
        showExistingMember(Boolean(result && result.simulated));
        return;
      }

      announce(MESSAGES.failure);
    }

    function clearError(key) {
      var message = root.querySelector('[data-kiolal-welcome-error="' + key + '"]');
      var field = fields[key];

      if (message) {
        message.textContent = '';
        message.hidden = true;
      }

      if (field) {
        field.removeAttribute('aria-invalid');
      }
    }

    function showError(key, text) {
      var message = root.querySelector('[data-kiolal-welcome-error="' + key + '"]');
      var field = fields[key];

      if (message) {
        message.textContent = text;
        message.hidden = false;
      }

      if (field) {
        field.setAttribute('aria-invalid', 'true');
      }
    }

    function validate() {
      var errors = [];

      Object.keys(fields).forEach(clearError);

      if (!fields.name.value.trim()) {
        errors.push({ key: 'name', text: MESSAGES.name });
      }

      var email = fields.email.value.trim();
      if (!email) {
        errors.push({ key: 'email', text: MESSAGES.emailEmpty });
      } else if (!EMAIL_PATTERN.test(email)) {
        errors.push({ key: 'email', text: MESSAGES.emailInvalid });
      }

      if (!fields.email_consent.checked) {
        errors.push({ key: 'email_consent', text: MESSAGES.emailConsent });
      }

      var localPhone = fields.phone.value.trim();
      var normalizedPhone = composeE164Phone(fields.country_code.value, localPhone);
      // 10/2026 welcome 40: email and text are both required for the code
      if (!localPhone) {
        errors.push({ key: 'phone', text: MESSAGES.phoneForSms });
      } else if (!normalizedPhone) {
        errors.push({ key: 'phone', text: MESSAGES.phoneInvalid });
      }

      if (!fields.sms_consent.checked) {
        errors.push({ key: 'sms_consent', text: MESSAGES.smsConsent });
      }

      errors.forEach(function (error) {
        showError(error.key, error.text);
      });

      if (errors.length) {
        announce(errors[0].text);
        var first = fields[errors[0].key];
        if (first && typeof first.focus === 'function') {
          first.focus();
        }
      }

      return errors.length === 0;
    }

    function trapFocus(event) {
      if (event.key !== 'Tab') {
        return;
      }

      var focusable = Array.prototype.filter.call(
        dialog.querySelectorAll(FOCUSABLE),
        function (node) {
          return node.offsetParent !== null;
        }
      );

      if (!focusable.length) {
        return;
      }

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    function onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        close({ suppressSession: true });
        return;
      }

      trapFocus(event);
    }

    function open() {
      if (isOpen) {
        return;
      }

      previousFocus = document.activeElement;
      root.hidden = false;
      root.setAttribute('data-open', 'true');
      document.body.classList.add('kiolal-welcome-open');
      isOpen = true;
      document.addEventListener('keydown', onKeydown, true);

      if (panel) {
        panel.scrollTop = 0;
      }

      if (closeButtons.length && typeof closeButtons[0].focus === 'function') {
        closeButtons[0].focus();
      }
    }

    function close(options) {
      if (!isOpen) {
        return;
      }

      var settings = options || {};

      root.hidden = true;
      root.removeAttribute('data-open');
      document.body.classList.remove('kiolal-welcome-open');
      isOpen = false;
      document.removeEventListener('keydown', onKeydown, true);

      if (!review.active && settings.suppressSession) {
        writeStorage('sessionStorage', SESSION_KEY, '1');
      }

      if (!review.active && settings.suppressPermanently) {
        writeStorage('localStorage', PERSISTENT_KEY, '1');
      }

      if (previousFocus && typeof previousFocus.focus === 'function') {
        previousFocus.focus();
      }
    }

    Array.prototype.forEach.call(closeButtons, function (button) {
      button.addEventListener('click', function () {
        close({ suppressSession: true });
      });
    });

    if (backdrop) {
      backdrop.addEventListener('click', function () {
        close({ suppressSession: true });
      });
    }

    Object.keys(fields).forEach(function (key) {
      var field = fields[key];
      if (!field) {
        return;
      }

      field.addEventListener('input', function () {
        idempotencyKey = null;
        clearError(key);
      });
      field.addEventListener('change', function () {
        idempotencyKey = null;
        clearError(key);
        if (key === 'country_code') {
          clearError('phone');
        }
      });
    });

    if (copyButton) {
      copyButton.addEventListener('click', function () {
        var code = root.getAttribute('data-discount-code') || '';

        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(code).then(function () {
            announce('The code ' + code + ' was copied.');
          }, function () {
            announce('Select the code ' + code + ' to copy it.');
          });
          return;
        }

        announce('Select the code ' + code + ' to copy it.');
      });
    }

    if (continueButton) {
      continueButton.addEventListener('click', function () {
        close({ suppressPermanently: true });
      });
    }


    form.addEventListener('submit', function (event) {
      event.preventDefault();

      if (!validate()) {
        return;
      }

      if (!idempotencyKey) {
        idempotencyKey = createIdempotencyKey();
      }

      if (!idempotencyKey) {
        announce(MESSAGES.failure);
        return;
      }

      submit.disabled = true;
      form.setAttribute('aria-busy', 'true');
      announce('');

      var normalizedPhone = composeE164Phone(fields.country_code.value, fields.phone.value);
      var submittedPayload = {
        name: fields.name.value.trim(),
        email: fields.email.value.trim(),
        phone: normalizedPhone || null,
        emailConsent: fields.email_consent.checked,
        smsConsent: Boolean(normalizedPhone) && fields.sms_consent.checked,
        idempotencyKey: idempotencyKey,
        source: 'kiolal-welcome-popup'
      };

      submitWelcomeSignup(submittedPayload, review, enrollmentEndpoint).then(function (result) {
        submit.disabled = false;
        form.removeAttribute('aria-busy');
        handleEnrollmentResult(result);
      }, function () {
        submit.disabled = false;
        form.removeAttribute('aria-busy');
        announce(MESSAGES.failure);
      });
    });

    // 10/2026 homepage: buttons marked data-kiolal-welcome-open open the same welcome pop-up on click.
    document.addEventListener('click', function (event) {
      if (!event.target.closest || !event.target.closest('[data-kiolal-welcome-open]')) {
        return;
      }
      event.preventDefault();
      showState('form');
      open();
    });

    if (review.active) {
      showState('form');
      window.requestAnimationFrame(open);
      return;
    }

    if (root.getAttribute('data-enabled') !== 'true') {
      return;
    }

    if (root.getAttribute('data-integration-ready') !== 'true') {
      return;
    }

    if (readStorage('localStorage', PERSISTENT_KEY)) {
      return;
    }

    if (readStorage('sessionStorage', SESSION_KEY)) {
      return;
    }

    var delay = parseInt(root.getAttribute('data-trigger-delay'), 10);
    if (isNaN(delay) || delay < 0) {
      return;
    }

    // The approved delay is measured from navigation, not from deferred-script
    // execution. This keeps a five-second invitation from drifting later when
    // storefront assets delay DOM initialization on a clean visitor session.
    var elapsedSinceNavigation = window.performance && typeof window.performance.now === 'function'
      ? window.performance.now()
      : 0;
    var remainingDelay = Math.max(0, delay - elapsedSinceNavigation);

    window.setTimeout(open, remainingDelay);
  }

  function init() {
    var root = document.querySelector('[data-kiolal-welcome]');

    if (root) {
      setupWelcome(root);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
